import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** Only the project row's manual toggle creates a newest-pulse scroll request. */
export function useManualProjectExpansion(
  expandedItems: Record<string, boolean>,
  toggleExpanded: (id: string) => void,
) {
  const nextIntent = useRef(0);
  const [manualExpandIntents, setManualExpandIntents] = useState<Record<string, number>>({});

  const completeManualExpansion = useCallback((projectId: string, intent: number) => {
    setManualExpandIntents(previous => {
      if (previous[projectId] !== intent) return previous;
      const next = { ...previous };
      delete next[projectId];
      return next;
    });
  }, []);

  const toggleProjectManually = (projectId: string) => {
    const expanding = !expandedItems[`project-${projectId}`];
    const intent = ++nextIntent.current;
    setManualExpandIntents(previous => {
      const next = { ...previous };
      if (expanding) next[projectId] = intent;
      else delete next[projectId];
      return next;
    });
    toggleExpanded(`project-${projectId}`);
  };

  // ESC/global collapse also cancels requests waiting for data.
  useLayoutEffect(() => {
    setManualExpandIntents(previous => {
      const cancelled = Object.keys(previous).filter(id => !expandedItems[`project-${id}`]);
      if (cancelled.length === 0) return previous;
      const next = { ...previous };
      cancelled.forEach(id => delete next[id]);
      return next;
    });
  }, [expandedItems]);

  return { manualExpandIntents, toggleProjectManually, completeManualExpansion };
}